import { useDismiss } from "@/common/hooks/useDismiss";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { Check, ChevronDown, Plus } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Espejo del MAX_ACCOUNTS_PER_USER backend (src/features/accounts/service.ts).
// Pre-disable la creación cuando ya alcanzaste el cap; el backend igual devuelve
// 409 MAX_ACCOUNTS_PER_USER si te lo saltás (defense-in-depth).
const MAX_ACCOUNTS_PER_USER = 50;

export function AccountSwitcher() {
  const { currentAccount, memberships, switchAccount } = useCurrentAccount();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useDismiss({
    enabled: open,
    containerRef,
    onDismiss: () => setOpen(false),
  });

  if (!currentAccount) return null;

  const atCap = memberships.length >= MAX_ACCOUNTS_PER_USER;
  const accountInitials = currentAccount.name.slice(0, 2).toUpperCase();

  function handleCreateNew() {
    setOpen(false);
    navigate("/onboarding/create-account");
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2.5 rounded-lg border border-app-border bg-app-surface-2 px-3 py-2.5 transition-colors hover:border-app-ink-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-app-emerald"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Cambiar de cuenta. Cuenta actual: ${currentAccount.name}`}
      >
        <div className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-app-ink-1 font-semibold text-[12px] text-white">
          {accountInitials}
        </div>
        <span className="min-w-0 flex-1 truncate text-left font-medium text-[13px] text-app-ink-1">
          {currentAccount.name}
        </span>
        <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-app-ink-3" aria-hidden />
      </button>

      {open && (
        <div className="absolute top-full right-0 left-0 z-50 mt-1 overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-lg">
          <ul aria-label="Cuentas disponibles">
            {memberships.map((m) => (
              <li
                key={m.accountId}
                aria-selected={m.accountId === currentAccount.id}
                className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-app-ink-1 text-sm transition-colors hover:bg-app-surface-2"
                onClick={() => {
                  setOpen(false);
                  if (m.accountId !== currentAccount.id) {
                    switchAccount(m.accountId);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setOpen(false);
                    if (m.accountId !== currentAccount.id) switchAccount(m.accountId);
                  }
                }}
              >
                {m.accountId === currentAccount.id && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-app-emerald" />
                )}
                <span className={m.accountId === currentAccount.id ? "ml-0" : "ml-[19px]"}>
                  {m.account.name}
                </span>
              </li>
            ))}
          </ul>
          <div className="border-app-border border-t">
            <button
              type="button"
              onClick={handleCreateNew}
              disabled={atCap}
              title={
                atCap
                  ? `Llegaste al límite de ${MAX_ACCOUNTS_PER_USER} cuentas. Contactá soporte.`
                  : undefined
              }
              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-app-ink-2 text-sm transition-colors hover:bg-app-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5 shrink-0 text-app-ink-3" aria-hidden />
              <span>Nueva cuenta</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
